import { useRef, useState, type ChangeEvent } from "react";
import { Button } from "primereact/button";
import { FormField } from "./FormField";
import { fetchRequestWithAuth } from "@/lib/api-client";
import { getAdminImagesUploadUrl } from "@/lib/config";
import { getRecordString } from "@/lib/type-guards";

interface ImageUploadFieldProps {
  id: string;
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
  error?: string;
  helperText?: string;
}

export function ImageUploadField({
  id,
  label,
  value,
  onChange,
  error,
  helperText,
}: ImageUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const url = getAdminImagesUploadUrl();

      const headers = new Headers();
      headers.set("Content-Type", file.type || "image/jpeg");

      const request = new Request(url, { method: "POST", headers, body: file });
      const response = await fetchRequestWithAuth(request);

      if (!response.ok) {
        let errorMessage = "Failed to upload image";
        try {
          const errorData: unknown = await response.json();
          const message = getRecordString(errorData, "error");
          if (message) {
            errorMessage = message;
          }
        } catch {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }

      const result: unknown = await response.json();
      const imageUrl = getRecordString(result, "imageUrl");
      if (!imageUrl) {
        throw new Error("Upload succeeded but no image URL was returned");
      }
      onChange(imageUrl);
      setUploadError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to upload image";
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const clearImage = () => {
    onChange(null);
  };

  return (
    <FormField
      id={id}
      label={label}
      error={error || uploadError || undefined}
      helperText={helperText || "Upload an image for the event"}
    >
      <div className="flex flex-column gap-3">
        {value ? (
          <div className="relative inline-block" style={{ maxWidth: "300px" }}>
            <img
              src={value}
              alt="Uploaded event"
              className="w-full border-round shadow-2"
              style={{ objectFit: "cover", maxHeight: "200px" }}
            />
            <Button
              type="button"
              icon="pi pi-times"
              className="p-button-rounded p-button-danger p-button-text absolute top-0 right-0 m-1"
              onClick={clearImage}
              aria-label="Clear image"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
            />
          </div>
        ) : (
          <div
            className="flex flex-column align-items-center justify-content-center p-4 border-2 border-dashed border-300 border-round cursor-pointer hover:border-primary transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="pi pi-image text-4xl text-400 mb-2"></i>
            <span className="text-600">Click to upload image</span>
            <input
              ref={fileInputRef}
              id={id}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handleUpload(e);
              }}
              disabled={uploading}
            />
          </div>
        )}
        {uploading && (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-spin pi-spinner text-primary"></i>
            <span className="text-sm text-600">Uploading image...</span>
          </div>
        )}
      </div>
    </FormField>
  );
}

